import React, { useEffect, useState } from 'react';
import { Facebook, Globe, Linkedin, Phone, FileText, Users, Instagram, Mail, Loader2, Table2 } from 'lucide-react';
import { getSourceOptions } from '@/actions/fetch-source-options';
import type { SourceOption } from '../types';

// Special constant for "Map from CSV" mode
export const SOURCE_MODE_CSV = '__map_from_csv__';

interface SourceSelectorProps {
  selectedSource: string;
  onSourceSelect: (sourceUuid: string, sourceName: string) => void;
}

// Icon mapping for different source types
const getIconForSource = (sourceName: string) => {
  const name = sourceName.toLowerCase();
  if (name.includes('facebook') || name.includes('fb')) return <Facebook className="h-8 w-8" />;
  if (name.includes('google') || name.includes('ads')) return <Mail className="h-8 w-8" />;
  if (name.includes('website') || name.includes('web')) return <Globe className="h-8 w-8" />;
  if (name.includes('linkedin')) return <Linkedin className="h-8 w-8" />;
  if (name.includes('instagram') || name.includes('insta')) return <Instagram className="h-8 w-8" />;
  if (name.includes('phone') || name.includes('call')) return <Phone className="h-8 w-8" />;
  if (name.includes('manual') || name.includes('entry')) return <FileText className="h-8 w-8" />;
  return <Users className="h-8 w-8" />;
};

const SourceSelector: React.FC<SourceSelectorProps> = ({ selectedSource, onSourceSelect }) => {
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch source column with options from API
      const response = await getSourceOptions();

      if (response.success && response.data?.columns) {
        // Find the source column
        const sourceColumn = response.data.columns.find(col => col.col_name === 'source');

        if (sourceColumn && sourceColumn.options && sourceColumn.options.length > 0) {
          // Map API options to SourceOption format with icons
          const sourcesWithIcons: SourceOption[] = sourceColumn.options
            .filter(opt => opt.active !== false) // Only show active options
            .map(option => ({
              value: option.uuid,
              label: option.opt_name,
              description: `Import leads from ${option.opt_name}`,
              icon: getIconForSource(option.opt_name)
            }));

          setSources(sourcesWithIcons);
        } else {
          setError('No source options found. Please configure source options in Form Fields settings.');
        }
      } else {
        setError('Failed to load source options from server.');
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load source options. Please try again.');
      setLoading(false);
      console.error('Error fetching sources:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-brandColor" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Select Lead Source
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose the source where these leads are coming from, or select "Map from CSV" to use source values from your CSV file.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Map from CSV Option - Always first */}
        <button
          type="button"
          onClick={() => onSourceSelect(SOURCE_MODE_CSV, 'Map from CSV')}
          className={`
            relative flex flex-col items-center p-6 rounded-lg border-2 transition-all duration-200
            hover:shadow-lg hover:scale-105
            ${
              selectedSource === SOURCE_MODE_CSV
                ? 'border-brandColor bg-cyan-50 dark:bg-cyan-900/20 ring-2 ring-cyan-500 ring-offset-2'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-cyan-300'
            }
          `}
        >
          <div
            className={`
              mb-3 p-3 rounded-full transition-colors
              ${
                selectedSource === SOURCE_MODE_CSV
                  ? 'bg-cyan-100 dark:bg-cyan-800 text-brandColor dark:text-cyan-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }
            `}
          >
            <Table2 className="h-8 w-8" />
          </div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Map from CSV
          </h4>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Use source column from your CSV file
          </p>
          {selectedSource === SOURCE_MODE_CSV && (
            <div className="absolute top-2 right-2">
              <div className="h-5 w-5 bg-brandColor rounded-full flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          )}
        </button>

        {sources.map((source) => (
          <button
            key={source.value}
            type="button"
            onClick={() => onSourceSelect(source.value, source.label)}
            className={`
              relative flex flex-col items-center p-6 rounded-lg border-2 transition-all duration-200
              hover:shadow-lg hover:scale-105
              ${
                selectedSource === source.value
                  ? 'border-brandColor bg-cyan-50 dark:bg-cyan-900/20 ring-2 ring-cyan-500 ring-offset-2'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-cyan-300'
              }
            `}
          >
            {/* Icon */}
            <div
              className={`
                mb-3 p-3 rounded-full transition-colors
                ${
                  selectedSource === source.value
                    ? 'bg-cyan-100 dark:bg-cyan-800 text-brandColor dark:text-cyan-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}
            >
              {source.icon}
            </div>

            {/* Label */}
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {source.label}
            </h4>

            {/* Description */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              {source.description}
            </p>

            {/* Selected indicator */}
            {selectedSource === source.value && (
              <div className="absolute top-2 right-2">
                <div className="h-5 w-5 bg-brandColor rounded-full flex items-center justify-center">
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedSource && (
        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
          <p className="text-sm text-cyan-800 dark:text-cyan-200">
            <span className="font-semibold">Selected:</span>{' '}
            {selectedSource === SOURCE_MODE_CSV
              ? 'Map from CSV - You will need to map a source column from your CSV file in the column mapping step.'
              : `${sources.find((s) => s.value === selectedSource)?.label} - All imported leads will be tagged with this source.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default SourceSelector;
